/**
 * Pure resolvers for appointment-note service/CPT dropdown options.
 * Provider entitlement is authoritative; calendar platform fallback is intentionally excluded.
 */
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';

export type ProviderSessionEnablementRow = AppointmentSessionType & {
  enabledForPractice: boolean;
  enabledForProvider: boolean;
};

/**
 * Effective provider-eligible clinical session ids for the note editor.
 * Requires provider allowlist; also requires practice enablement when the practice set is non-empty.
 */
export function resolveProviderEligibleSessionTypeIds(
  rows: ProviderSessionEnablementRow[],
): string[] {
  const practiceEnabledCount = rows.filter((r) => r.enabledForPractice).length;
  return rows
    .filter((r) => {
      if (!r.enabledForProvider) return false;
      if (practiceEnabledCount === 0) return true;
      return r.enabledForPractice;
    })
    .map((r) => r.id);
}

/**
 * Session types shown on the appointment note CPT/service dropdown:
 * active services assigned to the appointment provider, plus the appointment's
 * current HealthcareService even when inactive or no longer assigned.
 */
export function resolveNoteEditorSessionTypes(params: {
  allSessionTypes: AppointmentSessionType[];
  providerEligibleIds: string[];
  currentHealthcareServiceId?: string;
}): AppointmentSessionType[] {
  const byId = new Map(params.allSessionTypes.map((t) => [t.id, t]));

  const eligible: AppointmentSessionType[] = [];
  for (const id of params.providerEligibleIds) {
    const row = byId.get(id);
    if (row?.active) eligible.push(row);
  }

  const currentId = params.currentHealthcareServiceId?.trim();
  if (currentId && !eligible.some((t) => t.id === currentId)) {
    const current = byId.get(currentId);
    if (current) {
      return [current, ...eligible];
    }
  }

  return eligible;
}
